import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BsTrash
} from 'react-icons/bs';
import {
  IoStopCircleOutline
} from 'react-icons/io5';
import {
  MdCloudUpload,
  MdClose,
  MdDelete,
  MdVideoLibrary,
  MdRefresh
} from 'react-icons/md';

import { useUploadModal } from './useUploadModal';
import { UploadModalProps, FileUploadItem } from './types';
import styles from './UploadModal.module.css';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import { UPLOAD_INTERNAL_ONLY } from '../../constants/accessMessages';

/**
 * 動画アップロード用モーダルコンポーネント
 */
const getFileKey = (fileItem: FileUploadItem): string =>
  `${fileItem.file.name}-${fileItem.file.size}-${fileItem.file.lastModified}`;

const UploadModal = ({ isOpen = true, onClose, onUploadStart }: UploadModalProps) => {
  const {
    files,
    uploading,
    isDragging,
    fileInputRef,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleUpload,
    handleNormalClose,
    handleRemoveFile,
    handleOverlayClick,
    triggerFileInput,
    recoverableSessions,
    recoverSession,
    dismissSession,
    handleCancelUpload,
    cancelAllUploads
  } = useUploadModal(onClose, onUploadStart);

  // AC-UX2 (R2): 接続元分類をサーバから取得する。
  // isExternal===true の間はドロップゾーン・ファイル入力・送信ボタンをすべて非活性にして
  // UPLOAD_INTERNAL_ONLY を表示し、ファイル選択前に遠隔ユーザーを止める（R2-a）。
  // isLoading 中 / 取得失敗時（isExternal===false）はボタンを活性のままにして
  // サーバの requireInternalNetwork(403)に委ねる（フェイルクローズはサーバ側）。
  const { isExternal } = useOriginAccess();
  const [expandedFileNames, setExpandedFileNames] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    const currentKeys = new Set(files.map(getFileKey));
    setExpandedFileNames((prev) => {
      const nextKeys = Array.from(prev).filter((key) => currentKeys.has(key));
      const next = new Set(nextKeys);
      return next.size === prev.size ? prev : next;
    });
  }, [files]);

  const toggleFileName = (fileKey: string): void => {
    setExpandedFileNames((prev) => {
      const next = new Set(prev);
      if (next.has(fileKey)) {
        next.delete(fileKey);
      } else {
        next.add(fileKey);
      }
      return next;
    });
  };

  const modalContent = (
    <div
      className={`${styles['upload-modal-overlay']} ${!isOpen ? styles['hidden'] : ''}`}
      onClick={handleOverlayClick}
      data-testid="upload-modal__overlay"
    >
      <div className={styles['upload-modal-container']}>
        <button
          className={styles['upload-modal-close-button']}
          onClick={handleNormalClose}
          disabled={false}
          data-testid="upload-modal__close-button"
        >
          <MdClose />
        </button>

        <h2 className={styles['upload-modal-title']}>動画をアップロードする</h2>

        {/* アップロード中または一時停止中のファイルがある場合にキャンセル全件ボタンを表示 */}
        {files.some(f => f.status === 'uploading' || f.status === 'paused') && (
          <div className={styles['upload-modal-cancel-all-container']}>
            <button
              className={styles['upload-modal-cancel-all-button']}
              onClick={cancelAllUploads}
              title="すべてのアップロードをキャンセル"
              data-testid="upload-modal__cancel-all-button"
            >
              <IoStopCircleOutline className={styles['upload-modal-icon-spaced']} />
              すべてのアップロードをキャンセル
            </button>
          </div>
        )}

        {/* 復元可能セッションの表示 */}
        {recoverableSessions.length > 0 && files.length === 0 && (
          <div className={styles['upload-modal-recover-section']} data-testid="upload-modal__recover-section">
            <h3 className={styles['upload-modal-recover-title']}>前回のアップロードを再開</h3>
            <div className={styles['upload-modal-recover-list']}>
              {recoverableSessions.map(session => (
                <div key={session.sessionId} className={styles['upload-modal-recover-item']} data-testid={`upload-modal__recover-item-${session.sessionId}`}>
                  <div className={styles['upload-modal-recover-info']}>
                    <div className={styles['upload-modal-recover-file-icon']}>
                      <MdVideoLibrary />
                    </div>
                    <div className={styles['upload-modal-recover-details']}>
                      <span className={styles['upload-modal-recover-filename']}>{session.fileName}</span>
                      <span className={styles['upload-modal-recover-meta']}>
                        {(session.fileSize / (1024 * 1024)).toFixed(2)} MB - {session.progress}%
                      </span>
                    </div>
                  </div>
                  <div className={styles['upload-modal-recover-actions']}>
                    <button
                      onClick={() => recoverSession(session.sessionId)}
                      className={styles['upload-modal-recover-resume-button']}
                      title="再開"
                      data-testid={`upload-modal__recover-resume-${session.sessionId}`}
                    >
                      <MdRefresh className={styles['upload-modal-icon-spaced-sm']} />
                      再開
                    </button>
                    <button
                      onClick={() => dismissSession(session.sessionId)}
                      className={styles['upload-modal-recover-dismiss-button']}
                      title="破棄"
                      data-testid={`upload-modal__recover-dismiss-${session.sessionId}`}
                    >
                      <BsTrash className={styles['upload-modal-icon-spaced-sm']} />
                      破棄
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles['upload-modal-privacy-note']}>
          動画は <span>非公開</span> で保存されます。公開設定は後から変更できます。
        </div>

        {/* AC-UX2 (R2-a): 遠隔接続時の非活性バナー。ファイル選択前に表示する。 */}
        {isExternal && (
          <div
            className={styles['upload-modal-external-notice']}
            role="alert"
            aria-live="polite"
            data-testid="upload-modal__external-notice"
          >
            {UPLOAD_INTERNAL_ONLY}
          </div>
        )}

        <div
          className={`
            ${styles['upload-modal-drop-zone']}
            ${isDragging && !isExternal ? styles['upload-modal-drop-zone-dragging'] : ''}
            ${files.length > 0 ? styles['upload-modal-drop-zone-has-files'] : ''}
            ${isExternal ? styles['upload-modal-drop-zone-disabled'] : ''}
          `}
          onDrop={isExternal ? undefined : handleDrop}
          onDragOver={isExternal ? undefined : handleDragOver}
          onDragLeave={isExternal ? undefined : handleDragLeave}
          onClick={files.length === 0 && !isExternal ? triggerFileInput : undefined}
          aria-disabled={isExternal ? 'true' : undefined}
          data-testid="upload-modal__drop-zone"
        >
          {files.length > 0 ? (
            <ul className={styles['upload-modal-file-list']} data-testid="upload-modal__file-list">
              {files.map((fileItem, index) => {
                const fileKey = getFileKey(fileItem);
                const isFileNameExpanded = expandedFileNames.has(fileKey);
                const shouldShowToggle = fileItem.file.name.length > 30;

                return (
                  <li
                    key={index}
                    className={`
                      ${styles['upload-modal-file-item']} 
                      ${styles[`upload-modal-file-item-${fileItem.status}`]}
                    `}
                    data-testid={`upload-modal__file-item-${index}`}
                  >
                    <div className={styles['upload-modal-file-info']}>
                      <div className={styles['upload-modal-file-icon']}>
                        <MdVideoLibrary />
                      </div>
                      <div className={styles['upload-modal-file-details']}>
                        <div className={styles['upload-modal-file-name-row']}>
                          <span
                            className={`
                              ${styles['upload-modal-file-name']}
                              ${isFileNameExpanded
                                ? styles['upload-modal-file-name-expanded']
                                : styles['upload-modal-file-name-truncate']}
                            `}
                          >
                            {fileItem.file.name}
                          </span>
                          {shouldShowToggle && (
                            <button
                              type="button"
                              className={styles['upload-modal-file-name-toggle']}
                              onClick={() => toggleFileName(fileKey)}
                              aria-label={isFileNameExpanded ? '動画名を省略表示' : '動画名を全文表示'}
                            >
                              {isFileNameExpanded ? '省略' : '全文表示'}
                            </button>
                          )}
                        </div>
                        <span className={styles['upload-modal-file-size']}>
                          {(fileItem.file.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      </div>
                    </div>

                    <div className={styles['upload-modal-file-actions']}>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className={styles['upload-modal-remove-button']}
                        disabled={uploading}
                        title="削除"
                        data-testid={`upload-modal__remove-button-${index}`}
                      >
                        <MdDelete />
                      </button>
                    </div>

                    <div className={styles['upload-modal-progress-container']}>
                      <div
                        className={styles['upload-modal-progress-bar']}
                        data-status={fileItem.status}
                        style={{ width: `${fileItem.progress}%` }}
                        data-testid={`upload-modal__progress-bar-${index}`}
                      />
                    </div>

                    <div
                      className={styles['upload-modal-status-text']}
                      data-status={fileItem.status}
                      data-testid={`upload-modal__status-text-${index}`}
                    >
                      {renderStatusText(fileItem)}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={styles['upload-modal-drop-zone-content']}>
              <div className={styles['upload-modal-upload-icon']}>
                <MdCloudUpload />
              </div>
              {isExternal ? (
                /* 遠隔時: ドロップゾーン内にも非活性理由を明記（R2-a） */
                <p className={styles['upload-modal-drop-text']} data-testid="upload-modal__drop-external-text">
                  {UPLOAD_INTERNAL_ONLY}
                </p>
              ) : (
                <>
                  <p className={styles['upload-modal-drop-text']}>ここにファイルをドラッグアンドドロップ</p>
                  <p className={styles['upload-modal-drop-sub-text']}>または</p>
                  <label
                    className={styles['upload-modal-browse-button']}
                    htmlFor="upload-file-input"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="upload-modal__browse-button"
                  >
                    ファイルを選択
                  </label>
                </>
              )}
            </div>
          )}
        </div>

        {/* AC-UX2 (R2-a): 遠隔時はファイル入力自体を disabled にしてブラウザのファイル選択ダイアログも開かせない */}
        <input
          id="upload-file-input"
          type="file"
          accept="video/mp4"
          multiple
          onChange={handleFileChange}
          className={styles['upload-modal-file-input']}
          disabled={isExternal || (uploading && files.length >= 5)}
          ref={fileInputRef}
          data-testid="upload-modal__file-input"
          aria-disabled={isExternal ? 'true' : undefined}
        />

        <div className={styles['upload-modal-upload-actions']}>
          {files.length > 0 && (
            /* AC-UX2 (R2-a): 遠隔時はファイル追加ボタンも非活性 */
            <label
              className={`${styles['upload-modal-select-more-button']}${(isExternal || uploading || files.length >= 5) ? ` ${styles['upload-modal-select-more-button--disabled']}` : ''}`}
              htmlFor="upload-file-input"
              onClick={(e) => e.stopPropagation()}
              aria-disabled={isExternal ? 'true' : undefined}
              data-testid="upload-modal__select-more-button"
            >
              {files.length >= 5 ? '最大数に達しました' : 'ファイルを追加'}
            </label>
          )}

          {/*
           * AC-UX2 (R2-a, R2-c):
           * - isExternal 時に送信ボタンを disabled + aria-disabled にして院外からの送信を完全に遮断する。
           *   ファイル選択前（drop zone / input で弾く）でもここでも止める二重防護。
           * - disabled のため、遠隔でアップロードが進む状態自体が発生しない（R2-c の進捗完了表示問題も起きない）。
           * - 万一サーバ403が返った場合は useUploadModal の catch でスナックバーを出す既存処理に委ねる。
           */}
          <button
            className={`
              ${styles['upload-modal-upload-button']}
              ${uploading ? styles['upload-modal-upload-button-uploading'] : ''}
            `}
            onClick={handleUpload}
            disabled={isExternal || uploading || files.length === 0}
            aria-disabled={isExternal ? 'true' : undefined}
            title={isExternal ? UPLOAD_INTERNAL_ONLY : undefined}
            data-testid="upload-modal__upload-button"
          >
            <span className={styles['upload-modal-button-icon']}>
              <MdCloudUpload />
            </span>
            <span className={styles['upload-modal-button-text']}>
              {uploading ? 'アップロード中...' : 'アップロード'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default UploadModal;

/**
 * ファイル状態に基づいたステータステキストをレンダリングする
 */
const renderStatusText = (fileItem: FileUploadItem): string => {
  switch (fileItem.status) {
    case 'uploading':
      return fileItem.progress === 0 ? 'アップロード準備中...' : `${fileItem.progress}% アップロード中...`;
    case 'paused':
      return `一時停止中 (${fileItem.progress}%)`;
    case 'completed':
      return '完了';
    case 'error':
      return 'エラー';
    case 'cancelled':
      return 'キャンセル済み';
    case 'pending':
      return '準備完了';
    default:
      return '';
  }
}; 
